import { createStore, useStore as useVuexStore } from "vuex";
import countryCode from './countryCode.json';
import axios from 'axios';
import { inflate, deflate } from 'pako';
import router from '../router';
import { io } from "socket.io-client";
import { notification } from 'ant-design-vue';
import moment from 'moment';
import logger from '../utils/logger';
import { getDeviceHeaders } from '../utils/deviceInfo';
import { fetchPendingDepositActionItems, type DepositWalletActionItem } from '../components/Deposit/depositDetailsFlow';
import { formatAmountByAsset } from '../components/common';

const ACCESSTOKEN = 'accessToken';
const LST_ACCESS = 'last_access'
const USERINFO = 'userInfo';
const browserToken = 'browserToken';
const ZP_SOURCE = 'zpSource';
const UNION_ID = 'unionID';
// const DOMAIN = 'http://localhost:9090';
// const DOMAIN = 'https://uatapi.otcpro.io';
// const DOMAIN = 'http://192.168.168.199:10002/otc-core';

const DOMAIN = import.meta.env.VITE_API_BASE_URL || '';
const SOCKET = import.meta.env.VITE_SOCKET_BASE_URL || '';
const BASEURL = `${DOMAIN}/api/`;

const req = axios.create({
    baseURL: BASEURL,
    timeout: 60000,
});

req.interceptors.request.use((config) => {
    config.headers = {
        ...getDeviceHeaders(),
        ...(config.headers || {}),
    };
    return config;
});

export interface UserInfo {
    activated: number
    authenticate_guid:number
    disabled:number
    display_name:string
    email:string
    guid:string
    authenticate_referral_code:string|null,
    identification_type:string|null
    identification_status:string|null
    username:string|null
    initial:string
    authenticate_type:string|null
    /** 1 时法币提现超过阈值需填写 Source of Fund / Transaction Purpose */
    external?: number
    hear_about_otcpro_other?: string | null
}
export interface State {
    accessToken: string | null,
    last_access_str: string | null,
    userInfo: UserInfo | null,
    browserToken: string | null,
    currencies: Array<any> | null,
    socket: any | null,
    maintenance: any | null,
    mobileMenuOpen: boolean,
    isZpChannel: boolean,
    zpAuthLoading: boolean,
    unionID: string | null,
    /** Zp loginByZp 返回的 source（仅用于邮箱绑定弹窗预填，登出/绑定成功后清空） */
    zpSource: { Nickname?: string; Email?: string } | null,
    pendingDepositActionItems: DepositWalletActionItem[] | null,
    pendingDepositLoading: boolean,
}

interface OrderResultInfo {
    exchange_guid: string | null,
    instrument: string,
    deal_quantity: number,
    deal_price: number,
    order_type: string,
    side: string,
    status: number,
    user_guid: string,
    accept_slippage_bps: number,
    msg: string,
}
interface OrderResult {
    result: OrderResultInfo,
}

const store = createStore<State>({
    state: {
        accessToken: null,
        last_access_str: null,
        userInfo: null,
        browserToken: null,
        currencies: [],
        maintenance: {
            in_maintenance: false,
            results: []
        },
        socket: null,
        mobileMenuOpen: false,
        isZpChannel: false,
        zpAuthLoading: false,
        unionID: null,
        zpSource: null,
        pendingDepositActionItems: null,
        pendingDepositLoading: false,
    },
    getters: {
        accessToken: state => state.accessToken,
        last_access_str: state => state.last_access_str,
        userInfo: state => state.userInfo,
        currencies: state => state.currencies,
        domain: () => { return DOMAIN },
        socket:()=>{return SOCKET},
        countryCode: () => countryCode,
        inMaintenance: (state) => {
            return state.maintenance&&state.maintenance.in_maintenance?state.maintenance.in_maintenance:false;
        },
        maintenance: (state) => {
            return state.maintenance&&state.maintenance.results?state.maintenance.results[0]:null;
        },
        mobileMenuOpen: state => state.mobileMenuOpen,
        isZpChannel: state => state.isZpChannel,
        zpAuthLoading: state => state.zpAuthLoading,
        unionID: state => state.unionID,
        zpSource: state => state.zpSource,
        pendingDepositActionItems: state => state.pendingDepositActionItems ?? [],
        hasPendingDepositDetails: state => (state.pendingDepositActionItems?.length ?? 0) > 0,
        pendingDepositLoading: state => state.pendingDepositLoading,
    },
    mutations: {
        setIdentificationType(state, type:string|null = null) {
            if(state.userInfo) {
                state.userInfo.identification_type = type;
                if(type == null) {
                    state.userInfo.identification_status = null;
                }
            }
        },
        setDisplayName(state, display_name:string) {
            if(state.userInfo) {
                state.userInfo.display_name = display_name;
            }
        },
        setIdentificationStatus(state, status:string|null) {
            if(state.userInfo) {
                state.userInfo.identification_status = status;
            }
        },
        setMobileMenuOpenStatus(state, status = false) {
            state.mobileMenuOpen = status;
        },
        setZpChannel(state, isZp: boolean) {
            state.isZpChannel = isZp;
        },
        setZpAuthLoading(state, loading: boolean) {
            state.zpAuthLoading = loading;
        },
        setUnionID(state, id: string | null) {
            state.unionID = id;
            try {
                if (id) localStorage.setItem(UNION_ID, id);
                else localStorage.removeItem(UNION_ID);
            } catch (_) {}
        },
        setZpSource(state, source: { Nickname?: string; Email?: string } | null) {
            state.zpSource = source;
            try {
                if (source) {
                    localStorage.setItem(ZP_SOURCE, JSON.stringify(source));
                } else {
                    localStorage.removeItem(ZP_SOURCE);
                }
            } catch (_) {}
        },
        setPendingDepositActionItems(state, items: DepositWalletActionItem[] | null) {
            state.pendingDepositActionItems = items;
        },
        setPendingDepositLoading(state, loading: boolean) {
            state.pendingDepositLoading = loading;
        },
    },
    actions: {
        init() {
            try {
                axios.defaults.baseURL = BASEURL;
                Object.assign(axios.defaults.headers.common, getDeviceHeaders());
                this.state.last_access_str = localStorage.getItem(LST_ACCESS) || null;
                this.state.accessToken = localStorage.getItem(ACCESSTOKEN) || null;

                let _userInfo = localStorage.getItem(USERINFO) || null;
                if (!this.state.accessToken || this.state.accessToken == 'null' || this.state.accessToken == 'undefined' || this.state.accessToken.length == 0) {
                    this.dispatch("setAccessToken");
                }

                if (!_userInfo || _userInfo == 'null' || _userInfo == 'undefined') {
                    this.dispatch("setAccessToken");
                }
                else {
                    this.state.userInfo = JSON.parse(_userInfo);
                }
                try {
                    const _zp = localStorage.getItem(ZP_SOURCE);
                    if (_zp) this.state.zpSource = JSON.parse(_zp);
                } catch (_) {}
                try {
                    const _uid = localStorage.getItem(UNION_ID);
                    if (_uid) this.state.unionID = _uid;
                } catch (_) {}

                if (this.getters.isSuperAdmin) {
                    this.dispatch("getCompanies");
                }
                this.dispatch('getCurriencies');
            }
            catch (err) {
                this.dispatch("setAccessToken");
            }
        },
        /**
         * Zp 渠道：调用 window.zapry.request 或 window.ethereum.request({ method: "eth_mimo_union" }) 获取 union_id，
         * 与本地存储的 unionID 比对，若不一致则清除所有缓存（token/userInfo/unionID/zpSource 等）
         */
        async checkUnionIdAndClearIfMismatch() {
            logger.log('[eth_mimo_union] checkUnionIdAndClearIfMismatch 被调用');
            if (typeof window === 'undefined') {
                logger.log('[eth_mimo_union] 跳过：无 window');
                return;
            }
            const zapry = (window as any).zapry;
            const ethereum = (window as any).ethereum;
            const requestFn = zapry && typeof zapry.request === 'function' ? zapry.request.bind(zapry)
                : ethereum && typeof ethereum.request === 'function' ? ethereum.request.bind(ethereum)
                : null;
            if (!requestFn) {
                logger.log('[eth_mimo_union] 跳过：无 zapry.request 且无 ethereum.request');
                return;
            }
            try {
                const req = { method: "eth_mimo_union" };
                logger.log('[eth_mimo_union] 请求参数:', JSON.stringify(req, null, 2));
                const result = await requestFn(req);
                logger.log('[eth_mimo_union] 返回结果:', JSON.stringify(result, null, 2));
                const remoteUnionId = result?.union_id != null ? String(result.union_id).trim() : '';
                const localUnionId = this.state.unionID != null ? String(this.state.unionID).trim() : '';
                if (remoteUnionId !== localUnionId) {
                    this.dispatch("setAccessToken");
                }
            } catch (_) {}
        },
        setLastAccess(event) {
            localStorage.setItem(LST_ACCESS, moment().format());
        },
        setAccessToken(event, { token = null } = {}) {
            this.state.accessToken = token;

            if (this.state.accessToken) {
                localStorage.setItem(ACCESSTOKEN, this.state.accessToken);
            }
            else {
                localStorage.removeItem(ACCESSTOKEN);
                this.commit("setUnionID", null);
                this.commit("setZpSource", null);
                this.commit("setPendingDepositActionItems", null);
                this.commit("setPendingDepositLoading", false);
                this.dispatch("setUserInfo");
                this.dispatch("setbrowserToken");
            }
        },

        setbrowserToken(event, token = null) {
            this.state.browserToken = token;
            if (typeof token == 'string') {
                localStorage.setItem(browserToken, token);
            }
        },

        setUserInfo(event, { user = null, userInfo = null } = {}) {
            this.state.userInfo = user || userInfo;
            if (this.state.userInfo) {
                localStorage.setItem(USERINFO, JSON.stringify(this.state.userInfo));
            }
            else {
                localStorage.removeItem(USERINFO);
            }
        },
        request(event, { method = "GET", endpoint, headers = { 'Content-Type': 'application/json', Authorization: "" }, data, params, responseType }) {
            let ctrl = this;
            headers = { ...getDeviceHeaders(), ...headers };
            if (this.state.accessToken && !headers['Authorization']) {
                headers['Authorization'] = "Bearer " + this.state.accessToken;
            }
            let _data = data;

            let config = { 
                method, url: endpoint, headers, responseType, 
                data: _data, 
                // data,
                params 
            }
            // if (responseType) { config.responseType = responseType; }
            // if (data) { config.data = data; }
            // if (params) { config.params = params; }
            return new Promise((resolve, reject) => {
                req(config).then(d => {
                    if (d.data.__d) {
                        d.data = JSON.parse(inflate(d.data.__d, { to: 'string' }));
                    }
                    if(d.headers[`refresh-token`]) {
                        ctrl.dispatch("setAccessToken", { token: d.headers[`refresh-token`] });
                    }
                    resolve(d);
                }).catch(err => {
                    if (err.message && err.message.indexOf('timeout') >= 0) {
                        reject({
                            title: 'Request timeout',
                            message: 'Request timeout, please wait and try again.'
                        })
                    }
                    else if (err.response && err.response.data && err.response.data.name == "JWT_TOKEN_ERROR") {
                        ctrl.dispatch("setAccessToken");
                        router.replace('/session-expired');
                    }
                    else {
                        reject(err.response);
                    }
                })
            });
        },
        getError(event, { title = "UNKNOWN ERROR", message = "Unknown error", err = {} } = {}) {
            let result = {
                message: title || err.statusText,
                description: err.message || err.statusText || message
            }
            if (err.data.name) {
                result.message = err.data.name;
            }
            if (err.data.message) {
                result.description = err.data.message;
            }
            return result;
        },
        arrayBufferToBase64(event, buffer: ArrayBufferLike) {
            return new Promise((resolve, reject) => {
                try {
                    var arrayBufferView = new Uint8Array(buffer);
                    var blob = new Blob([arrayBufferView], { type: "image/jpeg" });
                    var urlCreator = window.URL || window.webkitURL;
                    resolve(urlCreator.createObjectURL(blob))
                } catch (err) {
                    reject(err);
                }

            })
        },
        getCurriencies(event) {
            let ctrl = this;
            ctrl.dispatch("request", {endpoint:"/currency", params: {
                enabled: 1
            }}).then(({ data }) => {
                ctrl.state.currencies = data.results;
            });
        },
        checkLoginStatus(event) {
            let ctrl = this;
            ctrl.dispatch("request", { endpoint:"authenticate/tkn" }).then(({ data }) => {
                if(data.vld != 1) {
                    ctrl.dispatch(`setAccessToken`);
                }
            });
        },
        checkMaintenance(event) {
            let ctrl = this;
            ctrl.dispatch("request", {endpoint:"/public/maintain"}).then(({ data }) => {
                ctrl.state.maintenance = data;
            });
        },
        /**
         * 获取用户信息并更新缓存
         * @param event - Vuex action event
         * @returns Promise<UserInfo> - 返回用户信息
         */
        getUserInfo(event) {
            let ctrl = this;
            return new Promise((resolve, reject) => {
                ctrl.dispatch("request", {
                    method: "GET",
                    endpoint: "authenticate/info"
                }).then(({ data }) => {
                    // 更新缓存中的用户信息
                    ctrl.dispatch("setUserInfo", { userInfo: data });
                    resolve(data);
                }).catch(err => {
                    reject(err);
                });
            });
        },
        clearPendingDepositDetails() {
            this.commit('setPendingDepositActionItems', []);
            this.commit('setPendingDepositLoading', false);
        },
        async fetchPendingDepositDetails() {
            if (!this.state.accessToken) {
                this.dispatch('clearPendingDepositDetails');
                return [];
            }
            this.commit('setPendingDepositLoading', true);
            try {
                const items = await fetchPendingDepositActionItems(this, formatAmountByAsset);
                this.commit('setPendingDepositActionItems', items);
                return items;
            } catch (_) {
                this.commit('setPendingDepositActionItems', []);
                return [];
            } finally {
                this.commit('setPendingDepositLoading', false);
            }
        },
        /**
         * Zp 渠道认证：与 eth_mimo_union 相同获取逻辑，requestFn(zapry 优先，否则 ethereum) → eth_mimo_auth 取 code → loginByZp 登录
         */
        async zpAuth(event) {
            const ctrl = this;
            if (typeof window === 'undefined') {
                throw new Error('Window is not defined');
            }
            const zapry = (window as any).zapry;
            const ethereum = (window as any).ethereum;
            const requestFn = zapry && typeof zapry.request === 'function' ? zapry.request.bind(zapry)
                : ethereum && typeof ethereum.request === 'function' ? ethereum.request.bind(ethereum)
                : null;
            if (!requestFn) {
                throw new Error('Ethereum not found');
            }

            const doLoginByZp = (code: string) => {
                return ctrl.dispatch("request", {
                    method: "GET",
                    endpoint: "authenticate/loginByZp",
                    params: { authCode: code }
                }).then(({ data }: { data: any }) => {
                    if (!data || !data.token || !data.user) {
                        throw new Error(data?.message || 'Login failed');
                    }
                    ctrl.dispatch("setAccessToken", { token: data.token });
                    ctrl.dispatch("setUserInfo", { userInfo: data.user });
                    ctrl.commit("setUnionID", data.user.unionID ?? null);
                    ctrl.commit("setZpSource", data.source ?? null);
                    return data;
                });
            };

            const clientId = import.meta.env.VITE_CLIENT_ID || 'YOUR_CLIENT_ID';
            const authReq = { method: "eth_mimo_auth", params: JSON.stringify({ client_id: clientId }) };
            logger.log('[eth_mimo_auth] 请求参数:', JSON.stringify(authReq, null, 2));
            try {
                const result = await requestFn(authReq);
                let resultStr: string;
                try {
                    resultStr = JSON.stringify(result, null, 2);
                } catch (_) {
                    resultStr = String(result);
                }
                logger.log('[eth_mimo_auth] 返回结果:', resultStr);
                const code = result && result.code;
                if (!code || typeof code !== 'string') {
                    throw new Error(result?.message || 'Invalid auth result');
                }
                // 用户拒绝或关闭弹窗时返回 code refuse / cancel，不发起 doLoginByZp 请求
                const codeLower = String(code).toLowerCase();
                if (codeLower === 'refuse' || codeLower === 'cancel') {
                    throw new Error('sign_in.zp_cancelled');
                }
                // 拿到 code 后才显示全局 loading（仅 doLoginByZp 请求阶段）
                ctrl.commit('setZpAuthLoading', true);
                try {
                    return await doLoginByZp(code);
                } finally {
                    ctrl.commit('setZpAuthLoading', false);
                }
            } catch (err: any) {
                // request 在 400 时 reject(err.response)，故 err 可能是 response 对象，取 err.data.message
                const msg = err?.response?.data?.message ?? err?.data?.message ?? err?.message;
                throw new Error(msg && typeof msg === 'string' ? msg : 'sign_in.zp_auth_failed');
            }
        },
    }
});

export function useStore(): typeof store {
    return useVuexStore();
}
export default store;
// export type Store = Omit<VuexStore<any>, "commit"> & {
//     commit<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(
//       key: K,
//       payload: P,
//       options?: CommitOptions
//     ): ReturnType<Mutations[K]>;
//    };